/**
 * PeopleYouMayKnow — the "Suggested" segment content on the Friends view.
 *
 * Self-contained: owns the suggestions query, session-local dismissals, and the optimistic
 * Add flow (sendFriendRequest → invalidate ['friends'] + ['friendSuggestions']). Renders null
 * for guests / while loading with no data; an empty-state panel when loaded-empty; an error
 * panel on failure; otherwise the labeled list of SuggestionRow.
 */

import React, { useState, useCallback } from 'react'
import { View, Text } from 'react-native'
import { UserPlus } from 'lucide-react-native'
import { useQueryClient } from '@tanstack/react-query'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useAnalytics } from '../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../src/analytics'
import { useFriendSuggestionsQuery } from '../../../src/friends/hooks/useFriendSuggestionsQuery'
import { sendFriendRequest } from '../../../src/friends/helpers/friendsApi'
import type { FriendSuggestion } from '../../../src/friends/types/friends.types'
import type { SnackbarVariant } from '../../../src/shared/stores/snackbarStore'
import { DataLoadErrorPanel } from '../../../src/shared/components/DataLoadErrorPanel/DataLoadErrorPanel'
import {
  FRIENDS_SUGGESTIONS_TEST_IDS,
  FRIENDS_SUGGESTIONS_COPY,
} from '../../../src/friends/constants/suggestions.constants'
import { SuggestionRow } from '../SuggestionRow'
import { peopleYouMayKnowStyles as styles } from './PeopleYouMayKnow.styles'

export interface PeopleYouMayKnowProps {
  userId: string
  /** Pass `signedIn && !isGuest`; when false the component renders null and does not query. */
  enabled: boolean
  onInvite: () => void
  onShowMessage?: (message: string, variant: SnackbarVariant) => void
  testID?: string
}

export const PeopleYouMayKnow: React.FC<PeopleYouMayKnowProps> = ({
  userId,
  enabled,
  onInvite,
  onShowMessage,
  testID,
}) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { track } = useAnalytics()
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set())

  const { data, isLoading, isError, refetch } = useFriendSuggestionsQuery(userId, enabled)

  const handleAdd = useCallback(
    async (suggestion: FriendSuggestion): Promise<boolean> => {
      track(ANALYTICS_EVENTS.FRIEND_SUGGESTION_ADD, {
        target_user_id: suggestion.id,
        mutual_friend_count: suggestion.mutualFriendCount,
        shared_squad_org_count: suggestion.sharedSquadOrgCount,
      })
      try {
        const result = await sendFriendRequest(suggestion.id, apiClient)
        if (!result.success) {
          onShowMessage?.(FRIENDS_SUGGESTIONS_COPY.ADD_FAILED, 'error')
          return false
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['friends', userId] }),
          queryClient.invalidateQueries({ queryKey: ['friendSuggestions', userId] }),
        ])
        onShowMessage?.(FRIENDS_SUGGESTIONS_COPY.ADD_SUCCESS, 'success')
        return true
      } catch {
        onShowMessage?.(FRIENDS_SUGGESTIONS_COPY.ADD_FAILED, 'error')
        return false
      }
    },
    [apiClient, queryClient, userId, track, onShowMessage]
  )

  const handleDismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  if (!enabled) return null

  if (isError) {
    return (
      <DataLoadErrorPanel
        title={FRIENDS_SUGGESTIONS_COPY.ERROR_TITLE}
        onRetry={() => void refetch()}
        testID={FRIENDS_SUGGESTIONS_TEST_IDS.ERROR}
      />
    )
  }

  const visible = (data ?? []).filter((s) => !dismissed.has(s.id))

  // Still loading and nothing cached yet → render nothing (page has its own skeleton region).
  if (isLoading && (data ?? []).length === 0) return null

  if (visible.length === 0) {
    return (
      <View style={styles.emptyWrap} testID={FRIENDS_SUGGESTIONS_TEST_IDS.EMPTY}>
        <View style={styles.emptyIconCircle}>
          <UserPlus size={30} color="#A78BFA" />
        </View>
        <Text style={styles.emptyTitle}>{FRIENDS_SUGGESTIONS_COPY.EMPTY_TITLE}</Text>
        <Text style={styles.emptyBody}>{FRIENDS_SUGGESTIONS_COPY.EMPTY_BODY}</Text>
        <SoundPressable
          tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
          onPress={onInvite}
          accessibilityRole="button"
          accessibilityLabel={FRIENDS_SUGGESTIONS_COPY.EMPTY_CTA}
          style={styles.ctaButton}
        >
          <Text style={styles.ctaLabel}>{FRIENDS_SUGGESTIONS_COPY.EMPTY_CTA}</Text>
        </SoundPressable>
      </View>
    )
  }

  return (
    <View style={styles.container} testID={testID ?? FRIENDS_SUGGESTIONS_TEST_IDS.SECTION}>
      <Text style={styles.sectionLabel}>{FRIENDS_SUGGESTIONS_COPY.SECTION_LABEL}</Text>
      <View style={styles.list}>
        {visible.map((s) => (
          <SuggestionRow key={s.id} suggestion={s} onAdd={handleAdd} onDismiss={handleDismiss} />
        ))}
      </View>
    </View>
  )
}
